import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faHome, 
  faUsers,
  faBars,
  faChevronDown,
  faTimes,
  faCreditCard,
  faCog,
  faClipboardList,
  faTableList,
  faExclamationTriangle,
  faChartLine,
  faWrench,
  faShieldHalved
} from '@fortawesome/free-solid-svg-icons'
import { useAdminNavigation } from '@/hooks/use-admin-navigation'

interface AdminSidebarProps {
  isCollapsed: boolean
  onToggle: () => void
}

interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentProps<typeof FontAwesomeIcon>['icon']
  children?: NavigationItem[]
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation()
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set())
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Close hover states when location changes (but keep dropdowns open)
  useEffect(() => {
    setTimeout(() => setHoveredItem(null), 0);
  }, [location.pathname])

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const toggleDropdown = (itemName: string) => {
    const newOpenDropdowns = new Set(openDropdowns)
    if (newOpenDropdowns.has(itemName)) {
      newOpenDropdowns.delete(itemName)
    } else {
      newOpenDropdowns.add(itemName)
    }
    setOpenDropdowns(newOpenDropdowns)
  }

  const isItemActive = (item: NavigationItem): boolean => {
    if (item.href === location.pathname) return true
    if (item.children) {
      return item.children.some(child => child.href === location.pathname)
    }
    return false
  }

  const handleMouseEnter = (itemName: string) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setHoveredItem(itemName)
  }

  const handleMouseLeave = () => {
    // Set a timeout to close the menu after 1 second
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null)
    }, 1000)
  }

  const { navigation } = useAdminNavigation()

  const renderNavigationItem = (item: NavigationItem) => {
    const isActive = isItemActive(item)
    const isDropdownOpen = openDropdowns.has(item.name)
    const hasChildren = item.children && item.children.length > 0

    if (isCollapsed) {
      // Collapsed view - icon only with hover mini menu for dropdowns
      return (
        <div
          key={item.name}
          className="relative"
        >
          {hasChildren ? (
            // Dropdown item in collapsed mode
            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnter(item.name)}
              onMouseLeave={handleMouseLeave}
            >
              {/* Extended hover area */}
              <div className="absolute inset-0 z-10" />

              <button
                className={`flex items-center justify-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                title={item.name}
              >
                 <FontAwesomeIcon
                   icon={item.icon}
                   className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                   }`}
                 />
              </button>

              {/* Mini menu for collapsed dropdowns */}
              {hasChildren && hoveredItem === item.name && item.children && (
                <div
                  className="absolute left-full top-0 ml-2 w-48 bg-popover rounded-lg shadow-lg border border-border py-2 z-50"
                  onMouseEnter={() => handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children.map((child) => {
                    const isChildActive = location.pathname === child.href
                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={`flex items-center px-4 py-2 text-sm font-medium transition-colors ${isChildActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={child.icon}
                          className={`mr-3 h-4 w-4 ${isChildActive ? 'text-primary' : 'text-muted-foreground'
                          }`}
                        />
                        {child.name}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            // Regular item in collapsed mode
            <Link
              to={item.href}
              className={`flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
              title={item.name}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
            </Link>
          )}
        </div>
      )
    } else {
      // Expanded view - full width with text
      return (
        <div key={item.name}>
          {hasChildren ? (
            // Dropdown item in expanded mode
            <div>
              <button
                onClick={() => toggleDropdown(item.name)}
                className={`flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon={item.icon}
                    className={`mr-3 h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  />
                  {item.name}
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && item.children && (
                <div className="mt-1 ml-4 space-y-1">
                  {item.children.map((child) => {
                    const isChildActive = location.pathname === child.href
                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isChildActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={child.icon}
                          className={`mr-3 h-4 w-4 ${isChildActive ? 'text-primary' : 'text-muted-foreground'
                          }`}
                        />
                        {child.name}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            // Regular item in expanded mode
            <Link
              to={item.href}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`mr-3 h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
              {item.name}
            </Link>
          )}
        </div>
      )
    }
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className={`hidden md:flex fixed left-0 top-0 h-screen bg-background shadow-sm border-r border-border transition-all duration-300 z-30 ${isCollapsed ? 'w-16' : 'w-64'
        }`}>
        <div className="flex flex-col w-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-border">
            <button
              onClick={onToggle}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <FontAwesomeIcon icon={faBars} className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigation.map(renderNavigationItem)}
          </nav>

          {/* Footer */}
          {!isCollapsed && (
            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
              <span>
                {/* <FontAwesomeIcon icon={faShieldAlt} className="mr-1" /> */}
                <span className="font-semibold text-primary">Motiv</span> Admin Panel
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className="md:hidden">
        {/* Mobile Toggle Button */}
        <button
          onClick={onToggle}
          className="fixed top-3 left-4 z-50 p-2 bg-background rounded-lg shadow-lg border border-border"
        >
          <FontAwesomeIcon icon={faBars} className="h-6 w-6 text-foreground" />
        </button>

        {/* Mobile Sidebar */}
        {!isCollapsed && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={onToggle}
            />

            {/* Sidebar */}
            <div className="fixed left-0 top-0 h-full w-auto bg-background shadow-lg z-50">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-border">
                  <button
                    onClick={onToggle}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                  </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2">
                  {navigation.map(renderNavigationItem)}
                </nav>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
                  <span>
                    {/* <FontAwesomeIcon icon={faShieldAlt} className="mr-1" /> */}
                    <span className="font-semibold text-primary">Motiv</span> Admin Panel
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default AdminSidebar
